import React, { useState } from 'react'
import orderBy from 'lodash/orderBy'
import format from 'date-fns/format'
import Grid from '@material-ui/core/Grid'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableRow from '@material-ui/core/TableRow'
import TableHead from '@material-ui/core/TableHead'
import TableSortLabel from '@material-ui/core/TableSortLabel'
import TablePagination from '@material-ui/core/TablePagination'
import InfoOutlined from '@material-ui/icons/InfoOutlined'
import EventDetailsModal from './EventDetailsModal'
import { useStyles } from './styles'

const EVENT_STATUSES_MAP = {
  live: 'Live',
  ongoing: 'Ongoing',
  ended: 'Ended',
  cancelled: 'Cancelled'
}

const headCells = [
  { id: 'name', disablePadding: true, label: 'Event name' },
  { id: 'start', disablePadding: false, label: 'Start' },
  { id: 'length', disablePadding: false, label: 'Event duration', align: 'center' },
  { id: 'hosts', disablePadding: false, label: 'Hosts' },
  { id: 'status', disablePadding: false, label: 'Status', align: 'center' },
  { id: 'info', disablePadding: false, label: 'Info', align: 'center' }
]

const EnhancedTableHead = ({ classes, order, orderByField, onRequestSort }) => {
  const createSortHandler = property => event => {
    onRequestSort(event, property)
  }

  return (
    <TableHead>
      <TableRow className={classes.headerRow}>
        {headCells.map(headCell => (
          <TableCell
            style={{ minWidth: headCell.minWidth || undefined }}
            key={headCell.id}
            align={headCell.align || 'left'}
            padding='none'
            sortDirection={orderByField === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderByField === headCell.id}
              direction={orderByField === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  )
}

const EventsGrid = ({ events }) => {
  const classes = useStyles()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [order, setOrder] = useState('asc')
  const [orderByField, setOrderBy] = useState('name')
  const [isModalOpen, setModalOpen] = useState(false)
  const [selectedEventId, setSelectedEventId] = useState(null)

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleRequestSort = (event, property) => {
    const isAsc = orderByField === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const handleModalClose = () => {
    setSelectedEventId(null)
    setModalOpen(false)
  }

  const getComparator = event => {
    switch (orderByField) {
      case 'name':
        return event.name.toLowerCase()
      case 'start':
        return event.dates[0]?.date ? event.dates[0]?.date : ''
      case 'length':
        return event.duration
      case 'status':
        return event.state
      default:
        return
    }
  }

  const handleOpenEventDetails = id => {
    setSelectedEventId(id)
    setModalOpen(true)
  }

  const emptyRows = rowsPerPage - Math.min(rowsPerPage, events.length - page * rowsPerPage)
  const data = orderBy(events, getComparator, order)

  return (
    <div className={classes.container}>
      <Grid container item xs={12} className={classes.table}>
        <TableContainer>
          <Table className={classes.table} size='small'>
            <EnhancedTableHead
              classes={classes}
              order={order}
              orderByField={orderByField}
              onRequestSort={handleRequestSort}
              rowCount={events.length}
            />
            <TableBody>
              {data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((event, index) => (
                <TableRow className={classes.tableRow} hover tabIndex={-1} key={`${event.id}${index}`}>
                  <TableCell align='left' padding='none'>
                    {event.name}
                  </TableCell>
                  <TableCell align='left' width={170}>
                    {event.dates.length ? format(event.dates[0]?.date, 'MM/dd/yyyy hh:mm a') : '-'}
                  </TableCell>
                  <TableCell align='center'>{event.duration}</TableCell>
                  <TableCell align='left'>{event.hosts?.map(host => `${host.firstName} ${host.lastName}`).join(', ')}</TableCell>
                  <TableCell align='center'>{event.datePublished ? EVENT_STATUSES_MAP[event.state] : ''}</TableCell>
                  <TableCell align='center'>
                    <InfoOutlined onClick={() => handleOpenEventDetails(event.id)} className={classes.icon} />
                  </TableCell>
                </TableRow>
              ))}
              {emptyRows > 0 && (
                <TableRow style={{ height: 50 * emptyRows }}>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          className={classes.pagination}
          rowsPerPageOptions={[5, 10, 25]}
          component='div'
          count={events.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
        {isModalOpen && <EventDetailsModal isModalOpen={isModalOpen} eventId={selectedEventId} handleModalClose={handleModalClose} />}
      </Grid>
    </div>
  )
}

export default EventsGrid
